import { apiTransport } from './api-client';
import type {
  AttentionItem,
  AttentionKind,
  PeopleMovementEntry,
  UpcomingHrEvent,
  WorkforceSummary,
} from '@micarahr/shared-types';

/**
 * The HR dashboard, from the server's aggregation.
 *
 * The prototype computed every figure in the browser by reading whole
 * collections and counting them. That is the wrong shape for a real dataset in
 * two separate ways: it needs the entire workforce delivered to produce a
 * headcount, and it needs the entire leave table delivered to count what is
 * pending. Both are answered by the server in one query each, under the
 * viewer's scope.
 *
 * One request, not eleven. A dashboard assembled from eleven parallel queries
 * spends its first second showing eleven independent spinners, and any one of
 * them failing leaves a panel that never resolves.
 */

interface ApiHrDashboard {
  workforce: {
    total: number;
    active: number;
    probationary: number;
    onLeaveToday: number;
    separationsInProgress: number;
    newHiresThisMonth?: number;
  };
  /*
   * The server's own key names, verbatim from /dashboards/hr. This interface
   * previously declared `pendingLeave`, `flaggedAttendance` and friends —
   * names the route never sends — so every count read as `undefined`, no
   * definition survived the `> 0` filter, and the attention centre rendered
   * an organisation with nothing to do regardless of what was pending.
   */
  attention: {
    leaveAwaitingDecision: number;
    overtimeAwaitingDecision: number;
    correctionsAwaitingDecision: number;
    attendanceAwaitingReview: number;
    documentsExpiringOrExpired: number;
    /* Split, so the item can say which; absent from an older server. */
    documentsExpiring?: number;
    documentsExpired?: number;
    probationReviewsOverdue: number;
  };
  /** Hires, regularisations and separations: the rows behind the counters. */
  movement?: PeopleMovementEntry[];
  /** Last working days, probation and contract ends, starts, holidays, birthdays. */
  upcoming?: UpcomingHrEvent[];
  updatedAt: string;
}

let inFlight: Promise<ApiHrDashboard> | null = null;

/**
 * Fetch once for both callers.
 *
 * `workforce()` and `attention()` are separate methods the screens call
 * together, and both read the same endpoint. Without this the dashboard makes
 * the same request twice on every render pass.
 */
async function load(): Promise<ApiHrDashboard> {
  inFlight ??= apiTransport()
    .get<ApiHrDashboard>('/api/v1/dashboards/hr')
    .finally(() => {
      // Cleared on the next tick so both synchronous callers share this one,
      // and a later refresh genuinely refetches.
      setTimeout(() => {
        inFlight = null;
      }, 0);
    });

  return inFlight;
}

/**
 * The attention centre.
 *
 * The server returns counts; the screen shows items with a severity and a
 * destination. Counts of zero are dropped rather than rendered as empty rows —
 * "0 pending corrections" is noise on a page whose whole job is to say what
 * needs doing today.
 */
function toAttentionItems(counts: ApiHrDashboard['attention']): AttentionItem[] {
  const definitions: {
    key: keyof ApiHrDashboard['attention'];
    kind: AttentionKind;
    issue: (n: number, all: ApiHrDashboard['attention']) => string;
    severity: AttentionItem['severity'];
    ownerLabel: string;
    href: string;
  }[] = [
    {
      key: 'attendanceAwaitingReview',
      kind: 'attendance_outside_geofence',
      issue: (n) => `${n} attendance record${n === 1 ? '' : 's'} flagged for review`,
      severity: 'high',
      ownerLabel: 'HR',
      href: '/attendance/review',
    },
    {
      key: 'correctionsAwaitingDecision',
      kind: 'pending_correction',
      issue: (n) => `${n} attendance correction${n === 1 ? '' : 's'} awaiting a decision`,
      severity: 'medium',
      ownerLabel: 'HR',
      href: '/attendance/corrections?status=open',
    },
    {
      key: 'leaveAwaitingDecision',
      kind: 'leave_pending_hr',
      issue: (n) => `${n} leave request${n === 1 ? '' : 's'} awaiting approval`,
      severity: 'medium',
      ownerLabel: 'HR',
      href: '/leave?status=open',
    },
    {
      key: 'overtimeAwaitingDecision',
      kind: 'overtime_pending',
      issue: (n) => `${n} overtime request${n === 1 ? '' : 's'} awaiting approval`,
      severity: 'medium',
      ownerLabel: 'HR',
      href: '/overtime?status=open',
    },
    {
      key: 'probationReviewsOverdue',
      kind: 'probation_review_due',
      issue: (n) => `${n} probation review${n === 1 ? '' : 's'} past its due date`,
      severity: 'high',
      ownerLabel: 'HR',
      href: '/probation?status=overdue',
    },
    {
      key: 'documentsExpiringOrExpired',
      kind: 'document_expiring',
      /*
       * Expiring and expired named apart. "13 employee documents expiring
       * soon" counted six that had already expired, beside a Documents page
       * showing Expiring 7 and Expired 6.
       */
      issue: (n, all) => {
        if (all.documentsExpiring === undefined || all.documentsExpired === undefined) {
          return `${n} employee document${n === 1 ? '' : 's'} expiring soon or expired`;
        }
        const expiring = all.documentsExpiring;
        const expired = all.documentsExpired;
        const parts = [
          expiring > 0 ? `${expiring} expiring soon` : null,
          expired > 0 ? `${expired} expired` : null,
        ].filter(Boolean);
        const lead = expiring > 0 ? expiring : expired;
        return `${lead} employee document${lead === 1 ? '' : 's'} ${parts
          .join(' · ')
          .replace(/^\d+ /, '')}`;
      },
      severity: 'low',
      ownerLabel: 'HR',
      href: '/documents?filter=expiring',
    },
  ];

  return definitions
    .filter((definition) => (counts[definition.key] ?? 0) > 0)
    .map((definition) => ({
      id: definition.key,
      kind: definition.kind,
      severity: definition.severity,
      // The aggregate answers "how many", not "who". A per-employee attention
      // list is a different endpoint; inventing a name here would be a lie the
      // screen renders confidently.
      employeeId: null,
      employeeName: null,
      issue: definition.issue(counts[definition.key] ?? 0, counts),
      // Ageing needs per-item timestamps the aggregate does not carry. Zero is
      // honest; a fabricated age would sort the list wrongly.
      ageHours: 0,
      ownerLabel: definition.ownerLabel,
      href: definition.href,
    }));
}

export const apiDashboardService = {
  async workforce(): Promise<WorkforceSummary> {
    const data = await load();

    return {
      totalEmployees: data.workforce.total,
      activeEmployees: data.workforce.active,
      newHiresThisMonth: data.workforce.newHiresThisMonth ?? 0,
      onProbation: data.workforce.probationary,
      onLeaveToday: data.workforce.onLeaveToday,
      separationsInProgress: data.workforce.separationsInProgress,
    } as WorkforceSummary;
  },

  async attention(): Promise<AttentionItem[]> {
    const data = await load();
    return toAttentionItems(data.attention);
  },

  async peopleMovement(): Promise<PeopleMovementEntry[]> {
    const data = await load();
    return data.movement ?? [];
  },

  async upcomingEvents(): Promise<UpcomingHrEvent[]> {
    const data = await load();
    return data.upcoming ?? [];
  },
};
